import { useEffect, useRef, useState } from "react";
import { BookOpen, CheckCheck, Eye, FileIcon, FileUp, Lock, MailCheck, Paperclip, Send, Sparkles } from "lucide-react";
import { API_URL } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Switch } from "./ui/switch";
import { Lightbox } from "./Lightbox";

function RequestUploader({ requestId, onFulfill, testPrefix }) {
  const [reqFiles, setReqFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const sendFiles = async () => {
    if (!reqFiles.length) return;
    setBusy(true);
    await onFulfill(requestId, reqFiles);
    setBusy(false);
    setReqFiles([]);
  };
  return (
    <div className="mt-3 flex items-center gap-2 flex-wrap">
      <Button type="button" size="sm" variant="outline" className="rounded-full bg-white" onClick={() => ref.current?.click()} data-testid={`${testPrefix}-req-attach-${requestId}`}>
        <Paperclip className="h-3.5 w-3.5 mr-1" /> Choose files
      </Button>
      <input ref={ref} type="file" multiple className="hidden" onChange={(e) => setReqFiles(Array.from(e.target.files || []))} />
      {reqFiles.length > 0 && <span className="text-xs text-neutral-500">{reqFiles.length} selected</span>}
      <Button type="button" size="sm" className="rounded-full" disabled={!reqFiles.length || busy} onClick={sendFiles} data-testid={`${testPrefix}-req-send-${requestId}`}>
        {busy ? "Uploading…" : "Send files"}
      </Button>
    </div>
  );
}

export function MessageThread({ messages, selfSender, onSend, sending, testPrefix = "thread", businessTools, onFulfillRequest }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [internal, setInternal] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const submit = async () => {
    if (!body.trim() && files.length === 0) return;
    const ok = await onSend(body, files, { internal });
    if (ok !== false) {
      setBody("");
      setFiles([]);
      setInternal(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const suggest = async () => {
    if (!businessTools?.onSuggest) return;
    setSuggesting(true);
    const draft = await businessTools.onSuggest();
    if (draft) setBody(draft);
    setSuggesting(false);
  };

  const fileRequest = async () => {
    if (!businessTools?.onFileRequest) return;
    await businessTools.onFileRequest(body.trim());
    setBody("");
  };

  const seenIdx =
    selfSender === "business"
      ? messages.reduce((acc, m, i) => (m.sender === "business" && !m.internal && !m.kind && m.read_by_customer ? i : acc), -1)
      : -1;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-4 p-4" data-testid={`${testPrefix}-messages`}>
        {messages.map((m, idx) => {
          const mine = m.sender === selfSender;
          if (m.kind === "dispatch" || m.kind === "confirmation" || m.kind === "activity") {
            const ok = m.kind === "confirmation" || m.kind === "activity" || m.dispatch_confirmed;
            return (
              <div key={m.id} className="flex justify-center" data-testid={`${testPrefix}-${m.kind}-${m.id}`}>
                <span className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 ${
                  ok ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-neutral-200 bg-neutral-100 text-neutral-500"
                }`}>
                  {m.kind === "confirmation" ? <CheckCheck className="h-3.5 w-3.5" /> : m.kind === "activity" ? <Eye className="h-3.5 w-3.5" /> : <MailCheck className="h-3.5 w-3.5" />}
                  {m.kind === "activity" && selfSender === "customer"
                    ? (m.body.includes("verified") ? "You verified your email and opened this conversation" : "You opened this conversation from the email link")
                    : m.body}
                  {m.kind === "dispatch" && (m.dispatch_confirmed ? " — read confirmed" : " — awaiting confirmation")}
                  {" · "}{new Date(m.created_at).toLocaleDateString()}
                </span>
              </div>
            );
          }
          if (m.kind === "file_request") {
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[78%] rounded-2xl border-2 border-dashed border-neutral-300 bg-white px-4 py-3" data-testid={`${testPrefix}-file-request-${m.id}`}>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                    <FileUp className="h-3.5 w-3.5" /> File request · {new Date(m.created_at).toLocaleString()}
                  </div>
                  <p className="text-sm text-neutral-900">{m.body}</p>
                  {m.fulfilled ? (
                    <span className="mt-2 inline-block text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">Fulfilled</span>
                  ) : selfSender === "customer" && onFulfillRequest ? (
                    <RequestUploader requestId={m.id} onFulfill={onFulfillRequest} testPrefix={testPrefix} />
                  ) : (
                    <span className="mt-2 inline-block text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">Awaiting files</span>
                  )}
                </div>
              </div>
            );
          }
          if (m.internal) {
            return (
              <div key={m.id} className="flex justify-end">
                <div className="max-w-[78%] rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid={`${testPrefix}-note-${m.id}`}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-700 mb-1">
                    <Lock className="h-3 w-3" /> Staff only · {new Date(m.created_at).toLocaleString()}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-amber-950">{m.body}</p>
                </div>
              </div>
            );
          }
          return (
            <div key={m.id}>
            <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[78%] rounded-2xl border px-4 py-3 ${
                  mine
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white text-neutral-900 border-[#E5E5E5]"
                }`}
              >
                <div className={`text-[10px] uppercase tracking-widest mb-1 flex items-center gap-2 flex-wrap ${mine ? "text-neutral-400" : "text-neutral-500"}`}>
                  <span>{m.sender === "business" ? "Business" : "Customer"}</span>
                  {m.via && <span className={`px-1.5 py-0.5 rounded-full border normal-case tracking-normal ${mine ? "border-neutral-600" : "border-sky-200 bg-sky-50 text-sky-700"}`}>via {m.via}</span>}
                  {m.broadcast && <span className={`px-1.5 py-0.5 rounded-full border normal-case tracking-normal ${mine ? "border-neutral-600" : "border-neutral-300 bg-neutral-100 text-neutral-600"}`}>broadcast</span>}
                  <span>· {new Date(m.created_at).toLocaleString()}</span>
                </div>
                {m.body && <p className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</p>}
                {m.attachments?.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {m.attachments.map((a) => (
                      <button
                        type="button"
                        key={a.filename}
                        onClick={() => setPreview({ url: `${API_URL}/uploads/${a.filename}`, original: a.original })}
                        data-testid={`${testPrefix}-attachment-${a.original}`}
                        className={`flex items-center gap-2 text-xs rounded-lg border px-2.5 py-1.5 transition-colors duration-150 w-full text-left ${
                          mine
                            ? "border-neutral-700 hover:bg-neutral-800 text-neutral-200"
                            : "border-[#E5E5E5] hover:bg-neutral-50 text-neutral-700"
                        }`}
                      >
                        <FileIcon className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{a.original}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {idx === seenIdx && (
              <div className="flex justify-end mt-1" data-testid={`${testPrefix}-seen`}>
                <span className="text-[11px] text-emerald-700 flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                  <Eye className="h-3 w-3" /> Customer opened & viewed{m.read_at_customer ? ` · ${new Date(m.read_at_customer).toLocaleString()}` : ""}
                </span>
              </div>
            )}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {preview && <Lightbox file={preview} onClose={() => setPreview(null)} />}
      <div className="border-t border-[#E5E5E5] p-4 space-y-3 bg-white">
        {internal && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700" data-testid={`${testPrefix}-internal-banner`}>
            <Lock className="h-3 w-3" /> Internal note — the customer won't see this
          </div>
        )}
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={internal ? "Write an internal note…" : "Write a reply…"}
          rows={3}
          className="resize-none"
          data-testid={`${testPrefix}-reply-input`}
        />
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              variant="outline"
              className="rounded-none border-[#E5E5E5]"
              onClick={() => fileRef.current?.click()}
              data-testid={`${testPrefix}-attach-button`}
            >
              <Paperclip className="h-4 w-4 mr-1.5" /> Attach
            </Button>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid={`${testPrefix}-file-input`}
            />
            {files.length > 0 && (
              <span className="text-xs text-neutral-500" data-testid={`${testPrefix}-files-count`}>
                {files.map((f) => f.name).join(", ")}
              </span>
            )}
            {businessTools && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" className="rounded-none border-[#E5E5E5]" data-testid={`${testPrefix}-canned-button`}>
                      <BookOpen className="h-4 w-4 mr-1.5" /> Templates
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white">
                    {(businessTools.cannedReplies || []).length === 0 && (
                      <DropdownMenuItem disabled>No templates yet</DropdownMenuItem>
                    )}
                    {(businessTools.cannedReplies || []).map((c) => (
                      <DropdownMenuItem key={c.id} onClick={() => setBody(c.body)} data-testid={`${testPrefix}-canned-${c.id}`}>
                        {c.title}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button type="button" variant="outline" className="rounded-none border-[#E5E5E5]" onClick={suggest} disabled={suggesting} data-testid={`${testPrefix}-suggest-button`}>
                  <Sparkles className="h-4 w-4 mr-1.5" /> {suggesting ? "Drafting…" : "AI draft"}
                </Button>
                <Button type="button" variant="outline" className="rounded-none border-[#E5E5E5]" onClick={fileRequest} data-testid={`${testPrefix}-file-request-button`}>
                  <FileUp className="h-4 w-4 mr-1.5" /> Request file
                </Button>
                <label className="flex items-center gap-2 text-xs text-neutral-500 ml-1">
                  <Switch checked={internal} onCheckedChange={setInternal} data-testid={`${testPrefix}-internal-toggle`} />
                  Note
                </label>
              </>
            )}
          </div>
          <Button onClick={submit} disabled={sending} className="rounded-full px-6" data-testid={`${testPrefix}-send-button`}>
            <Send className="h-4 w-4 mr-1.5" /> {sending ? "Sending…" : internal ? "Save note" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}
